<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1700px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-row items-end justify-between gap-4 mb-6 flex-wrap">
      <div>
        <div class="text-xs text-gray-400 font-medium">Purchasing</div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Shipments</h1>
        <p class="text-sm text-gray-500 mt-1">Inbound containers carrying purchase orders - from port of loading to the yard</p>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
      <a [routerLink]="['/purchases']" class="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold shadow-sm hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 transition-colors" matTooltip="Back to purchase orders">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-gray-400 group-hover:text-amber-600">receipt_long</mat-icon>Purchase orders
        <mat-icon class="!text-[16px] !h-4 !w-4 text-gray-300 group-hover:text-amber-600 -mr-1">arrow_forward</mat-icon>
      </a>
      <button
        class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
        (click)="onCreate()">
        <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
          <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
        </span>
        New shipment
      </button>
      </div>
    </div>

    <!-- Headline figures - shortcuts to the shipments they count (except "due soon") -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <button class="group relative overflow-hidden text-left rounded-2xl bg-neutral-900 text-white p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all"
        [ngClass]="status === 'IN TRANSIT' && !lateOnly ? 'ring-4 ring-sky-200' : ''" (click)="showInTransit()">
        <mat-icon class="absolute -right-3 -bottom-4 !text-[96px] !h-24 !w-24 !text-white/5 pointer-events-none">directions_boat</mat-icon>
        <div class="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">At sea</div>
        <div class="text-3xl font-bold mt-1.5 leading-none po-num-tab">{{ summary.inTransit }}</div>
        <div class="text-xs text-neutral-400 mt-2 flex items-center gap-1 group-hover:text-sky-300 transition-colors">Shipments in transit <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>

      <div class="rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Due soon</span>
          <span class="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">event_upcoming</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none text-gray-900 po-num-tab">{{ summary.arrivingSoon }}</div>
        <div class="text-xs text-gray-400 mt-2">Arriving in the next 7 days</div>
      </div>

      <button class="group text-left rounded-2xl bg-white border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" [ngClass]="lateOnly ? 'border-red-300 ring-4 ring-red-50' : 'border-gray-100'" (click)="showLate()">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Running late</span>
          <span class="h-8 w-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">schedule</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none po-num-tab" [ngClass]="summary.late ? 'text-red-600' : 'text-gray-900'">{{ summary.late }}</div>
        <div class="text-xs text-gray-400 mt-2 flex items-center gap-1 group-hover:text-red-600 transition-colors">Past ETA, not at port <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>

      <button class="group text-left rounded-2xl bg-white border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" [ngClass]="status === 'ARRIVED' && !lateOnly ? 'border-amber-300 ring-4 ring-amber-50' : 'border-gray-100'" (click)="showAwaitingClearance()">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">At port</span>
          <span class="h-8 w-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">anchor</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none text-gray-900 po-num-tab">{{ summary.awaitingClearance }}</div>
        <div class="text-xs text-gray-400 mt-2 flex items-center gap-1 group-hover:text-amber-700 transition-colors">Arrived, awaiting clearance <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>
    </div>

    @if (loadError) {
    <div class="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between gap-3">
      <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cloud_off</mat-icon>Couldn't load shipments from the server.</span>
      <button class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-red-200 hover:bg-red-100" (click)="load()">Try again</button>
    </div>
    }

    <!-- View: shipments, or every container on its own row -->
    <div class="flex items-center justify-between gap-3 mb-4 flex-wrap">
      <div class="inline-flex items-center rounded-2xl bg-white border border-gray-200 p-1 shadow-sm" role="tablist" aria-label="View">
        <button type="button" role="tab" class="flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-xl text-sm font-semibold transition-all"
          [ngClass]="view === 'shipments' ? 'bg-neutral-900 text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'"
          [attr.aria-selected]="view === 'shipments'" (click)="setView('shipments')">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="view === 'shipments' ? '!text-amber-400' : ''">directions_boat</mat-icon>Shipments
        </button>
        <button type="button" role="tab" class="flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-xl text-sm font-semibold transition-all"
          [ngClass]="view === 'containers' ? 'bg-neutral-900 text-white shadow-md' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'"
          [attr.aria-selected]="view === 'containers'" (click)="setView('containers')">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [ngClass]="view === 'containers' ? '!text-amber-400' : ''">view_in_ar</mat-icon>Containers
        </button>
      </div>
      <span class="text-xs text-gray-400 hidden md:block">{{ view === 'containers' ? 'Every container on its own row - say or pick a state right there' : 'Shipments with their containers underneath' }}</span>
    </div>

    @if (view === 'containers') {
    <app-containers-list />
    } @else {
    <!-- List card -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      <!-- Status tabs -->
      <div class="px-3 sm:px-5 border-b border-gray-100 overflow-x-auto">
        <div class="flex items-center gap-1 min-w-max">
          @for (tab of statusTabs; track tab) {
          <button class="relative flex items-center gap-2 px-3 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap"
            [ngClass]="status === tab ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'"
            (click)="setStatus(tab)">
            @if (tab !== 'ALL') { <span class="h-2 w-2 rounded-full" [ngClass]="statusDot(tab)"></span> }
            <span class="capitalize">{{ tab === 'ALL' ? 'All shipments' : (tab | lowercase) }}</span>
            <span class="rounded-full px-1.5 min-w-[22px] text-center text-[11px] po-num-tab" [ngClass]="status === tab ? 'bg-neutral-900 text-amber-400' : 'bg-gray-100 text-gray-500'">{{ tabCount(tab) }}</span>
            @if (status === tab) { <span class="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-amber-400"></span> }
          </button>
          }
        </div>
      </div>

      <!-- Search -->
      <div class="px-4 sm:px-5 pt-4 pb-3 flex flex-col lg:flex-row lg:items-center gap-3">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input
            #searchBox
            type="text"
            placeholder="Search by shipment, container, BL, invoice no., shipping line or vessel"
            class="w-full pl-11 pr-16 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            [(ngModel)]="search"
            (ngModelChange)="onSearchInput()"
            (keydown.enter)="applyFilters()"
            (keydown.escape)="clearSearch()" />
          @if (search) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          } @else {
          <kbd class="hidden sm:block absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-400 border border-gray-200 border-b-2 rounded-md px-1.5 py-0.5 bg-white pointer-events-none" title="Press / to search">/</kbd>
          }
        </div>
        @if (filtersActive) {
        <button class="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 px-2.5 py-2 rounded-lg hover:bg-red-50 transition-colors self-start lg:self-auto" (click)="onResetFilters()">
          <mat-icon class="!text-[16px] !h-4 !w-4">filter_alt_off</mat-icon> Clear all
        </button>
        }
      </div>

      <!-- Spreadsheet: export / import -->
      <div class="px-4 sm:px-5 pb-4 flex items-center gap-3 flex-wrap border-b border-gray-100">
        <input #importInput type="file" class="hidden" accept=".xlsx,.xls,.csv" (change)="onImportFile($event)" />
        <span class="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 mr-1 hidden sm:inline">Spreadsheet</span>

        <button type="button"
          class="group inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
          [disabled]="exporting" (click)="exportSheet()" matTooltip="Download the shipments matching your filters as an Excel sheet">
          <span class="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center shadow-sm shadow-emerald-200 group-hover:scale-105 transition-transform">
            @if (exporting) { <mat-spinner diameter="14" class="export-spinner"></mat-spinner> } @else { <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">download</mat-icon> }
          </span>
          <span class="text-left leading-tight">
            <span class="block text-sm font-bold text-gray-900">Export</span>
            <span class="block text-[10px] font-medium text-gray-400 po-num-tab">{{ count }} shipment{{ count === 1 ? '' : 's' }} &middot; .xlsx</span>
          </span>
        </button>

        <button type="button"
          class="group inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-xl bg-white border border-gray-200 shadow-sm hover:border-sky-300 hover:shadow-md hover:shadow-sky-100 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
          [disabled]="importing" (click)="importInput.click()" matTooltip="Upload an edited export to create or update shipments - you'll see every change first">
          <span class="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-500 text-white flex items-center justify-center shadow-sm shadow-sky-200 group-hover:scale-105 transition-transform">
            @if (importing) { <mat-spinner diameter="14" class="export-spinner"></mat-spinner> } @else { <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">upload</mat-icon> }
          </span>
          <span class="text-left leading-tight">
            <span class="block text-sm font-bold text-gray-900">Import</span>
            <span class="block text-[10px] font-medium text-gray-400">Update from a sheet</span>
          </span>
        </button>


        <span class="ml-auto hidden lg:flex items-center gap-1.5 text-[11px] text-gray-400">
          <mat-icon class="!text-[14px] !h-3.5 !w-3.5">info</mat-icon>
          Edit in Excel and import it back - nothing saves until you've checked the preview
        </span>
      </div>


      @if (lateOnly) {
      <div class="px-5 py-2.5 bg-red-50/60 border-b border-red-100 flex items-center gap-2 text-xs">
        <mat-icon class="!text-[16px] !h-4 !w-4 !text-red-500">schedule</mat-icon>
        <span class="font-semibold text-red-700">Showing shipments past their ETA and not yet at port</span>
        <button class="ml-auto font-semibold text-red-700 hover:text-red-900 flex items-center gap-0.5" (click)="lateOnly = false; applyFilters()">
          Show all <mat-icon class="!text-[14px] !h-3.5 !w-3.5">close</mat-icon>
        </button>
      </div>
      }

      <!-- Column headings (wide screens) -->
      <div class="hidden lg:grid sh-list-cols gap-4 pl-7 pr-5 py-2.5 bg-gray-50/80 border-b border-gray-100 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
        <div class="flex items-center">
          <button type="button" class="sh-check" [class.on]="allOnPageSelected" [class.mixed]="someOnPageSelected" (click)="toggleSelectPage()" (keydown.enter)="$event.stopPropagation()" [attr.aria-pressed]="allOnPageSelected" [attr.aria-label]="allOnPageSelected ? 'Unselect this page' : 'Select this page'">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ allOnPageSelected ? 'check' : 'remove' }}</mat-icon>
          </button>
        </div>
        <div>Shipment</div><div>Carrier</div><div>Voyage</div><div>Containers</div><div>Purchase orders</div><div class="text-right">Value</div><div>State</div><div></div>
      </div>

      @if (loading && shipments.length === 0) {
        @for (r of skeletonRows; track r) {
        <div class="lg:grid sh-list-cols gap-4 pl-7 pr-5 py-5 border-b border-gray-50 items-center animate-pulse">
          <div class="hidden lg:block"><div class="h-[18px] w-[18px] rounded-md bg-gray-100"></div></div>
          <div><div class="h-3.5 w-20 bg-gray-200 rounded"></div><div class="h-2.5 w-14 bg-gray-100 rounded mt-2"></div></div>
          <div class="flex items-center gap-3 mt-3 lg:mt-0"><div class="h-9 w-9 rounded-xl bg-gray-100"></div><div class="h-3.5 w-24 bg-gray-200 rounded"></div></div>
          <div class="hidden lg:block"><div class="h-3 w-40 bg-gray-100 rounded"></div><div class="h-1.5 w-full bg-gray-100 rounded mt-3"></div></div>
          <div class="hidden lg:block"><div class="h-5 w-28 bg-gray-100 rounded-md"></div></div>
          <div class="hidden lg:block"><div class="h-5 w-16 bg-gray-100 rounded-full"></div></div>
          <div class="hidden lg:block"><div class="h-3.5 w-16 bg-gray-200 rounded ml-auto"></div></div>
          <div class="hidden lg:block"><div class="h-5 w-16 bg-gray-100 rounded-full"></div></div>
          <div></div>
        </div>
        }
      } @else if (shipments.length === 0) {
        <div class="min-h-80 flex flex-col justify-center items-center text-center px-6 py-14">
          <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">{{ filtersActive ? 'search_off' : 'directions_boat' }}</mat-icon></span>
          <div class="text-lg font-semibold text-gray-800">{{ filtersActive ? 'No shipments match' : 'No shipments yet' }}</div>
          <div class="text-sm text-gray-500 mt-1 max-w-sm">{{ filtersActive ? 'Try a different search, or clear the filters to see everything.' : 'Book the first inbound shipment to track containers from port to yard.' }}</div>
          @if (filtersActive) {
          <button class="mt-4 px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700" (click)="onResetFilters()">Clear filters</button>
          } @else {
          <button class="mt-4 px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700" (click)="onCreate()">New shipment</button>
          }
        </div>
      } @else {

      <!-- Rows -->
      <div class="transition-opacity" [class.opacity-50]="loading">
        @for (s of shipments; track s.id) {
        <div class="po-list-row group relative lg:grid sh-list-cols gap-4 pl-7 pr-5 py-4 items-center border-b border-gray-50 last:border-b-0 cursor-pointer transition-colors"
          [ngClass]="isSelected(s) ? 'bg-amber-50/70 hover:bg-amber-50' : 'hover:bg-amber-50/40'"
          tabindex="0" role="link" [attr.aria-label]="'Open ' + s.reference"
          (click)="onOpen(s)" (keydown.enter)="onOpen(s)">
          <span class="absolute left-0 top-3 bottom-3 w-1 rounded-r-full" [ngClass]="statusDot(s.status)"></span>

          <!-- Select -->
          <div class="absolute left-2.5 top-5 lg:static flex items-center">
            <button type="button" class="sh-check" [class.on]="isSelected(s)" [class.mixed]="false" (click)="toggleSelect(s, $event)" (keydown.enter)="$event.stopPropagation()" [attr.aria-pressed]="isSelected(s)" [attr.aria-label]="(isSelected(s) ? 'Unselect ' : 'Select ') + s.reference">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ isSelected(s) ? 'check' : 'remove' }}</mat-icon>
          </button>
          </div>

          <!-- Shipment -->
          <div class="min-w-0 flex items-start justify-between lg:block gap-3">
            <div class="min-w-0">
              <div class="font-bold text-gray-900 group-hover:text-amber-700 transition-colors">{{ s.reference }}</div>
              <div class="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1"><mat-icon class="!text-[12px] !h-3 !w-3">warehouse</mat-icon>{{ s.store }}</div>
              @if (s.invoiceNumber) {
              <div class="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 max-w-full" [matTooltip]="'Invoice ' + s.invoiceNumber + (s.invoiceDate ? ' · ' + (s.invoiceDate | date:'d MMM yyyy') : '')">
                <mat-icon class="!text-[12px] !h-3 !w-3 !text-gray-400 flex-shrink-0">receipt_long</mat-icon><span class="truncate font-mono">{{ s.invoiceNumber }}</span>
              </div>
              }
            </div>
            <div class="lg:hidden flex-shrink-0">
              <span class="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap inline-block" [ngClass]="getStatusClass(s.status)">{{ s.status | lowercase }}</span>
            </div>
          </div>

          <!-- Carrier -->
          <div class="min-w-0 flex items-center gap-3 mt-3 lg:mt-0">
            <span class="h-9 w-9 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">sailing</mat-icon></span>
            <div class="min-w-0">
              <div class="font-semibold text-gray-900 truncate" [ngClass]="!s.shippingLine ? '!text-gray-300 font-normal' : ''">{{ s.shippingLine || 'No shipping line' }}</div>
              <div class="text-xs text-gray-400 truncate" [matTooltip]="s.vesselDetails || ''">{{ s.vesselDetails || 'No vessel details' }}</div>
            </div>
          </div>

          <!-- Voyage -->
          <div class="min-w-0 mt-3 lg:mt-0">
            <div class="flex items-center justify-between gap-2 text-[13px] font-semibold text-gray-800">
              <span class="truncate" [ngClass]="!s.portOfLoading ? 'text-gray-300 font-normal' : ''">{{ s.portOfLoading || 'Loading port?' }}</span>
              <span class="truncate text-right" [ngClass]="!s.portOfDischarge ? 'text-gray-300 font-normal' : ''">{{ s.portOfDischarge || 'Discharge port?' }}</span>
            </div>
            <div class="sh-mini-track my-1.5 mx-1">
              <span class="sh-mini-fill" [class.done]="atPort(s)" [style.width.%]="journeyPct(s) ?? 0"></span>
              <span class="sh-mini-end" style="left: 0" [class.on]="!!journeyPct(s)" [class.done]="atPort(s)"></span>
              <span class="sh-mini-end" style="left: 100%" [class.done]="atPort(s)"></span>
              @if (journeyPct(s) !== null && s.status !== 'CANCELLED') {
              <span class="sh-mini-ship" [style.left.%]="journeyPct(s)"><mat-icon class="!text-[12px] !h-3 !w-3">{{ atPort(s) ? 'anchor' : 'directions_boat' }}</mat-icon></span>
              }
            </div>
            <div class="flex items-center justify-between gap-2 text-[11px]">
              <span class="text-gray-400 po-num-tab">ETD {{ s.etd ? (s.etd | date:'d MMM') : '-' }}</span>
              @if (daysToEta(s); as d) {
                @if (d < 0) {
                <span class="font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-px">{{ -d }}d late</span>
                } @else if (d <= 7) {
                <span class="font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-px">in {{ d }} day{{ d === 1 ? '' : 's' }}</span>
                }
              } @else if (daysToEta(s) === 0) {
              <span class="font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-px">due today</span>
              }
              <span class="font-semibold text-gray-700 po-num-tab">ETA {{ s.eta ? (s.eta | date:'d MMM yyyy') : '-' }}</span>
            </div>
          </div>

          <!-- Containers: a compact summary - the full breakdown opens below the row -->
          <div class="min-w-0 mt-3 lg:mt-0">
            @if (s.containers?.length) {
            <button type="button"
              class="sh-ctr group/c w-full text-left rounded-xl border px-2.5 py-2 transition-all outline-none"
              [ngClass]="expanded.has(s.id) ? 'border-amber-300 bg-amber-50/70 shadow-sm' : 'border-gray-200 bg-white hover:border-amber-300 hover:shadow-sm'"
              (click)="toggleExpand(s.id, $event)" (keydown.enter)="$event.stopPropagation()"
              [attr.aria-expanded]="expanded.has(s.id)" [matTooltip]="expanded.has(s.id) ? 'Hide containers' : 'Show what is in each container'">
              <div class="flex items-center gap-2.5 min-w-0">
                <span class="h-8 min-w-[32px] px-1.5 rounded-lg bg-neutral-900 text-amber-400 text-sm font-bold flex items-center justify-center po-num-tab flex-shrink-0">{{ s.containers!.length }}</span>
                <span class="min-w-0 flex-1 leading-tight">
                  <span class="block text-[13px] font-semibold text-gray-900 whitespace-nowrap">Container{{ s.containers!.length === 1 ? '' : 's' }}</span>
                  <span class="block text-[11px] text-gray-400 whitespace-nowrap po-num-tab">{{ shipmentUnits(s) | number: '1.0-2' }} units</span>
                </span>
                <span class="h-6 w-6 rounded-full flex items-center justify-center text-gray-400 group-hover/c:bg-amber-100 group-hover/c:text-amber-700 transition-colors flex-shrink-0">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] transition-transform duration-200" [class.rotate-180]="expanded.has(s.id)">expand_more</mat-icon>
                </span>
              </div>
              <!-- One segment per container, in its state's colour -->
              <div class="mt-2 flex gap-0.5 h-1.5 rounded-full overflow-hidden">
                @for (c of s.containers!; track c.id; let ci = $index) {
                <span class="flex-1" [ngClass]="statusDot(containerState(c, s))"
                  [matTooltip]="(c.containerNo || 'Container ' + (ci + 1)) + ' · ' + (containerState(c, s) | titlecase)"></span>
                }
              </div>
              <div class="mt-1.5 text-[10px] font-semibold whitespace-nowrap po-num-tab" [ngClass]="containerSummary(s).tone">{{ containerSummary(s).text }}</div>
            </button>
            } @else if (containerList(s).length) {
            <div class="flex flex-wrap items-center gap-1">
              @for (c of containerList(s); track $index) {
              <span class="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-1.5 py-0.5">
                <mat-icon class="!text-[12px] !h-3 !w-3 !text-gray-400">view_in_ar</mat-icon>{{ c }}
              </span>
              }
            </div>
            } @else {
            <span class="text-xs text-gray-300">No container no. yet</span>
            }
            @if (s.billOfLading) {
            <div class="mt-1 text-[11px] text-gray-400 truncate">BL <span class="font-mono text-gray-500">{{ s.billOfLading }}</span></div>
            }
          </div>

          <!-- Purchase orders -->
          <div class="flex flex-wrap gap-1 mt-3 lg:mt-0">
            @for (o of s.orders; track o.id) {
            <button class="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-100 hover:bg-amber-100 hover:border-amber-200 transition-colors" [matTooltip]="o.supplierName || 'Open purchase order'" (click)="onOpenOrder(o.id, $event)">{{ o.reference }}</button>
            }
            @if (s.orders.length === 0) { <span class="text-xs text-gray-300">No POs yet</span> }
          </div>

          <!-- Value -->
          <div class="lg:text-right leading-tight mt-3 lg:mt-0">
            @for (t of s.totals; track t.currency) {
            <div class="font-bold text-gray-900 po-num-tab whitespace-nowrap">{{ currencySymbol(t.currency) }}{{ t.total | number:'1.2-2' }} <span class="text-[10px] font-semibold text-gray-400">{{ t.currency }}</span></div>
            }
            @if (s.totals.length === 0) { <span class="text-gray-300">&mdash;</span> }
            @if (s.itemCount) { <div class="text-[11px] text-gray-400 mt-0.5">{{ s.itemCount }} line{{ s.itemCount === 1 ? '' : 's' }}</div> }
          </div>

          <!-- State -->
          <div class="hidden lg:block">
            <span class="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap inline-block" [ngClass]="getStatusClass(s.status)">{{ s.status | lowercase }}</span>
          </div>

          <!-- Actions -->
          <div class="absolute right-3 bottom-3 lg:static flex justify-end">
            <button class="h-9 w-9 rounded-full text-gray-400 hover:bg-white hover:text-gray-800 hover:shadow-sm flex items-center justify-center transition-all"
              [matMenuTriggerFor]="rowMenu" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()" aria-label="Shipment actions" matTooltip="Actions">
              <mat-icon class="!text-[20px]">more_horiz</mat-icon>
            </button>
            <mat-menu #rowMenu="matMenu" xPosition="before">
              <button mat-menu-item (click)="onOpen(s)"><mat-icon>edit_square</mat-icon><span>Open shipment</span></button>
              @for (o of s.orders; track o.id) {
              <button mat-menu-item (click)="onOpenOrder(o.id)"><mat-icon>receipt_long</mat-icon><span>Open {{ o.reference }}</span></button>
              }
              @if (canDelete) {
              <div class="my-1 border-t border-gray-100"></div>
              <button mat-menu-item (click)="confirmDelete(s)"><mat-icon class="!text-red-500">delete_outline</mat-icon><span class="text-red-600">Delete shipment</span></button>
              }
            </mat-menu>
          </div>
        </div>

        <!-- Container breakdown -->
        @if (expanded.has(s.id) && s.containers?.length) {
        <div class="pl-7 pr-5 pb-5 pt-1 bg-amber-50/30 border-b border-gray-100">
          <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            @for (c of s.containers!; track c.id; let ci = $index) {
            <div class="group/card rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:border-amber-300 hover:shadow-md transition-all"
              role="link" tabindex="0" [attr.aria-label]="'Open container ' + (c.containerNo || ci + 1)"
              (click)="openContainer(c.id)" (keydown.enter)="openContainer(c.id)">
              <div class="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 group-hover/card:bg-amber-50/40 transition-colors">
                <span class="h-7 w-7 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[15px] !h-[15px] !w-[15px]">view_in_ar</mat-icon></span>
                <div class="min-w-0 leading-tight">
                  <div class="font-mono text-xs font-bold text-gray-900 truncate">{{ c.containerNo || 'Container ' + (ci + 1) }}</div>
                  <div class="text-[10px] text-gray-400 po-num-tab">{{ c.lines?.length ?? 0 }} SKU{{ (c.lines?.length ?? 0) === 1 ? '' : 's' }} &middot; {{ containerUnits(c) | number: '1.0-2' }} units</div>
                </div>
                <!-- State: say it, or pick it -->
                <div class="ml-auto flex items-center gap-1 flex-shrink-0" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()">
                  @if (containerState(c, s) === 'RECEIVED') {
                  <span class="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border whitespace-nowrap" [ngClass]="getStatusClass('RECEIVED')" matTooltip="Checked in at the yard - stock booked">
                    <mat-icon class="!text-[11px] !h-[11px] !w-[11px]">lock</mat-icon>Received
                  </span>
                  } @else {
                  @if (containerState(c, s) !== 'DELIVERED') {
                  <button type="button" class="ctr-mic h-7 w-7 rounded-full flex items-center justify-center transition-all"
                    [ngClass]="listeningContainerId === c.id ? 'bg-red-500 text-white is-listening' : savingContainerId === c.id ? 'bg-gray-100 text-gray-400' : 'bg-violet-50 text-violet-600 hover:bg-violet-100'"
                    [disabled]="savingContainerId === c.id" (click)="speakContainerState(c, s)"
                    [matTooltip]="listeningContainerId === c.id ? 'Listening - say a state, e.g. “cleared”' : 'Say its new state'" aria-label="Say this container's new state">
                    <mat-icon class="!text-[15px] !h-[15px] !w-[15px] relative z-10">{{ listeningContainerId === c.id ? 'graphic_eq' : 'mic' }}</mat-icon>
                  </button>
                  }
                  <button type="button" class="inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full pl-2 pr-1 py-0.5 border capitalize whitespace-nowrap hover:shadow-sm transition-shadow"
                    [ngClass]="getStatusClass(containerState(c, s))" [matMenuTriggerFor]="ctrStateMenu" [disabled]="savingContainerId === c.id" matTooltip="Change state">
                    {{ containerState(c, s) | lowercase }}<mat-icon class="!text-[13px] !h-[13px] !w-[13px] opacity-60">expand_more</mat-icon>
                  </button>
                  <mat-menu #ctrStateMenu="matMenu" xPosition="before">
                    @for (st of containerStateOptions; track st.value) {
                    <button mat-menu-item (click)="setContainerState(c, s, st.value)" [disabled]="st.value === containerState(c, s)">
                      <span class="inline-flex items-center gap-2.5"><span class="h-2 w-2 rounded-full" [ngClass]="statusDot(st.value)"></span>{{ st.label }}</span>
                    </button>
                    }
                  </mat-menu>
                  }
                </div>
              </div>
              @if (c.clearedAt) {
              <div class="px-3 pt-2 text-[10px] font-semibold text-violet-700 flex items-center gap-1"><mat-icon class="!text-[12px] !h-3 !w-3">verified</mat-icon>Cleared {{ c.clearedAt | date: 'd MMM yyyy' }}</div>
              }
              <div class="px-3 py-2 flex flex-col divide-y divide-gray-50">
                @for (l of c.lines ?? []; track $index) {
                <div class="py-1.5 flex items-baseline gap-2 min-w-0">
                  <div class="min-w-0 flex-1">
                    @if (l.sku) {
                    <a class="block font-mono text-[11px] text-gray-700 truncate hover:text-amber-700 hover:underline underline-offset-2" [routerLink]="['/inventory']" [queryParams]="{ q: l.sku }" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()" [matTooltip]="'Show ' + l.sku + ' in inventory'">{{ l.sku }}</a>
                    } @else {
                    <div class="font-mono text-[11px] text-gray-400">-</div>
                    }
                    <div class="text-[10px] text-gray-400 truncate" [matTooltip]="l.productName">{{ l.productName }}</div>
                  </div>
                  <span class="text-xs font-bold text-gray-900 whitespace-nowrap po-num-tab">{{ l.quantity | number: '1.0-2' }} <span class="text-[10px] font-medium text-gray-400">{{ l.unitOfMeasure }}</span></span>
                </div>
                } @empty {
                <div class="py-2 text-[11px] text-gray-300">Empty</div>
                }
              </div>
            </div>
            }
          </div>
        </div>
        }
        }
      </div>

      <mat-paginator
        [length]="count"
        [pageSize]="itemsPerPage"
        [pageIndex]="pageNumber"
        [pageSizeOptions]="[10, 20, 50]"
        showFirstLastButtons
        (page)="updateList($event)">
      </mat-paginator>
      }
    </div>
    }

  </div>

  <!-- Bulk actions for the selected shipments -->
  @if (selected.size && view === 'shipments') {
  <div class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]">
    <div class="flex items-center gap-1 rounded-2xl bg-neutral-900/95 backdrop-blur text-white pl-4 pr-2 py-2 shadow-2xl shadow-black/30 ring-1 ring-white/10">
      <span class="flex items-center gap-2 pr-3 mr-1 border-r border-white/10 whitespace-nowrap">
        <span class="h-6 min-w-[24px] px-1.5 rounded-full bg-amber-400 text-neutral-900 text-xs font-bold flex items-center justify-center po-num-tab">{{ selected.size }}</span>
        <span class="text-sm font-semibold">selected</span>
      </span>
      <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-50" [disabled]="bulkBusy" (click)="exportSelected()">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-emerald-400">download</mat-icon><span class="hidden sm:inline">Export</span>
      </button>
      <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-50" [disabled]="bulkBusy" [matMenuTriggerFor]="bulkStateMenu">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-sky-400">swap_horiz</mat-icon><span class="hidden sm:inline">Set state</span>
        <mat-icon class="!text-[16px] !h-4 !w-4 opacity-60 -ml-0.5">expand_less</mat-icon>
      </button>
      <mat-menu #bulkStateMenu="matMenu" yPosition="above">
        @for (st of bulkStates; track st.value) {
        <button mat-menu-item (click)="bulkSetState(st)">
          <span class="inline-flex items-center gap-2.5"><span class="h-2 w-2 rounded-full" [ngClass]="statusDot(st.value)"></span>{{ st.label }}</span>
        </button>
        }
      </mat-menu>
      @if (canDelete) {
      <button class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-red-300 hover:bg-red-500/15 hover:text-red-200 transition-colors disabled:opacity-50" [disabled]="bulkBusy" (click)="bulkDelete()">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">delete_outline</mat-icon><span class="hidden sm:inline">Delete</span>
      </button>
      }
      @if (bulkBusy) { <mat-spinner diameter="18" class="export-spinner mx-2"></mat-spinner> }
      <button class="ml-1 h-8 w-8 rounded-full text-white/60 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors" (click)="clearSelection()" matTooltip="Clear selection" aria-label="Clear selection">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
      </button>
    </div>
  </div>
  }
  </div>
</app-main-layout>
